import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, TwoColumnLayout } from '@factorialco/f0-react'
import { CardPin } from '@factorialco/f0-react/icons/app'
import type { PurchaseRequestDetailQuery } from 'generated/resources/graphql'
import { PurchaseRequestFragment, TimelineItemEmployeesDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TimelineItem from 'modules/approvals_v2/components/TimelineItem'
import PurchaseRequestSummarySidebar from 'modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestSummarySidebar'

type ApprovalsV2ApprovalTimelineEntry =
  PurchaseRequestDetailQuery['procurement']['approvalsTimelines'][number]['entries'][number]

const calculateDaysOverdue = (dueOn: string | null | undefined): number => {
  if (!dueOn) return 0

  const deadlineDate = stringToDate(dueOn, 'backend')
  const daysOverdue = moment().startOf('day').diff(moment(deadlineDate).startOf('day'), 'days')

  return daysOverdue > 0 ? daysOverdue : 0
}

interface Props {
  purchaseRequest: PurchaseRequestFragment
  timelineEntries: ApprovalsV2ApprovalTimelineEntry[]
}

const WorkflowApprovalLayout = ({ purchaseRequest, timelineEntries }: Props) => {
  const { employee } = useCurrentSession()
  const approvalProcess = purchaseRequest?.approvalProcess
  const reviewRequests = approvalProcess?.reviewRequests || []

  const employeeIds = useMemo(
    () =>
      timelineEntries
        .flatMap((entry) => entry.assignees || [])
        .map((assignee) => assignee.employeeId)
        .filter((id): id is number => id != null),
    [timelineEntries]
  )

  const { data: employeesData } = useQuery(TimelineItemEmployeesDocument, {
    variables: { ids: employeeIds },
    skip: employeeIds.length === 0,
  })

  const employees = employeesData?.employees?.employeeNamesConnection?.nodes || []

  const formatAlertDate = (timestamp: string) => {
    const date = stringToDate(timestamp, 'backend_datetime')
    return i18nDateToString(date, 'ui_short_with_time')
  }

  const isAutoRejected =
    approvalProcess?.decision === 'rejected' &&
    !approvalProcess?.decisionReason &&
    !reviewRequests.some((reviewRequest) => reviewRequest.decision === 'rejected')

  const automaticRejectionAlert =
    isAutoRejected && approvalProcess?.updatedAt ? (
      <F0Alert
        variant="critical"
        title={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.title'
        )}
        description={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.description',
          {
            date: formatAlertDate(approvalProcess.updatedAt),
          }
        )}
        icon={CardPin}
      />
    ) : null

  const hasPendingReviewRequests = reviewRequests.some((reviewRequest) => !reviewRequest.decision)
  const isCurrentUserRequester = approvalProcess?.requesterId === employee.id
  const isCurrentUserReviewer = !isCurrentUserRequester && hasPendingReviewRequests

  const getDaysOverdue = (entry: ApprovalsV2ApprovalTimelineEntry): number | undefined => {
    if (entry.status === 'running' && entry.dueOn) {
      return calculateDaysOverdue(entry.dueOn)
    }
    return undefined
  }

  const getAlertForEntry = (
    entry: ApprovalsV2ApprovalTimelineEntry,
    daysOverdue: number | undefined
  ) => {
    const { actionGroup, status, decisionReason, time: timestamp } = entry

    // Overdue alert
    if (daysOverdue !== undefined && daysOverdue > 0) {
      const alertTitle = isCurrentUserReviewer
        ? i18n.tp('procurement.purchase_requests.detail.timeline.overdue_alert.assignee.title', {
            count: daysOverdue,
          })
        : i18n.tp('procurement.purchase_requests.detail.timeline.overdue_alert.title', {
            count: daysOverdue,
          })

      return <F0Alert variant="warning" title={alertTitle} description="" />
    }

    if (actionGroup === 'approvals' && decisionReason) {
      const isRejected = status === 'rejected'
      const isChangesRequested = status === 'changesrequested'

      if (!isRejected && !isChangesRequested) {
        return undefined
      }

      const decision = isRejected ? 'rejected' : 'changes_requested'

      const matchingReviewRequest = reviewRequests.find(
        (reviewRequest) =>
          reviewRequest.decisionReason === decisionReason && reviewRequest.decision === decision
      )

      const reviewerTime = matchingReviewRequest?.reviewedAt || timestamp

      // Get reviewer name from assignees
      let reviewerName = ''
      if (entry.assignees && entry.assignees.length > 0) {
        const firstAssignee = entry.assignees[0]
        if (firstAssignee?.peopleGroupName) {
          reviewerName = firstAssignee?.peopleGroupName ?? ''
        } else if (firstAssignee?.employeeId) {
          const employee = employees.find((emp) => emp?.id === firstAssignee?.employeeId)
          reviewerName = employee?.fullName || ''
        }
      }

      return (
        <F0Alert
          variant={isRejected ? 'critical' : 'warning'}
          title={
            isRejected
              ? i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
                  reason: decisionReason,
                })
              : i18n.t(
                  'procurement.purchase_requests.detail.timeline.changes_requested_alert.title',
                  {
                    reason: decisionReason,
                  }
                )
          }
          description={`${reviewerName}, ${formatAlertDate(reviewerTime)}`}
          icon={CardPin}
        />
      )
    }

    return undefined
  }

  const mainContent = (
    <Box gap="s24">
      {automaticRejectionAlert}
      <Box gap="s8">
        {timelineEntries.map((entry, index) => {
          const daysOverdue =
            entry.status === 'running' && entry.dueOn ? getDaysOverdue(entry) : undefined

          return (
            <TimelineItem
              key={index}
              entry={entry}
              hasConnectingLine={index !== timelineEntries.length - 1}
              alert={getAlertForEntry(entry, daysOverdue)}
              daysOverdue={daysOverdue}
              employees={employees}
            />
          )
        })}
      </Box>
    </Box>
  )

  const sideContent = <PurchaseRequestSummarySidebar purchaseRequest={purchaseRequest} />

  return <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
}

export default WorkflowApprovalLayout
